import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import Button from "../common/Button";
import Input from "../common/Input";
import { useAuth } from "../../context/AuthContext";
import { useAgencyAuth } from "../../context/AgencyAuthContext";
import Loading from "../common/Loading";
import { FingerPrintIcon } from "@heroicons/react/24/outline";

type ChallengeMethod = "totp" | "email" | "webauthn" | "backup";

// Wrapper that provides the right setMfaVerified based on context
const MFAChallenge: React.FC<{ isAgency?: boolean }> = ({ isAgency = false }) => {
  return isAgency ? <AgencyMFAChallenge /> : <UserMFAChallenge />;
};

const AgencyMFAChallenge: React.FC = () => {
  const { setMfaVerified } = useAgencyAuth();
  return <MFAChallengeInner isAgency setMfaVerified={setMfaVerified} />;
};

const UserMFAChallenge: React.FC = () => {
  const { setMfaVerified } = useAuth();
  return <MFAChallengeInner isAgency={false} setMfaVerified={setMfaVerified} />;
};

const MFAChallengeInner: React.FC<{
  isAgency: boolean;
  setMfaVerified: (v: boolean) => void;
}> = ({ isAgency, setMfaVerified }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [method, setMethod] = useState<ChallengeMethod>("totp");
  const [availableMethods, setAvailableMethods] = useState<string[]>([]);
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [trustedDeviceDays, setTrustedDeviceDays] = useState(0);
  const [showBackup, setShowBackup] = useState(false);

  useEffect(() => {
    fetchStatusAndInitiate();
  }, []);

  const fetchStatusAndInitiate = async () => {
    try {
      const { data } = await axios.get("/api/mfa/status");
      setAvailableMethods(data.methods.map((m: any) => m.method));
      setTrustedDeviceDays(data.trustedDeviceDays || 0);

      // Auto-select the primary or first available method
      const methods = data.methods.map((m: any) => m.method);
      if (methods.includes("webauthn")) {
        setMethod("webauthn");
      } else if (methods.includes("totp")) {
        setMethod("totp");
      } else if (methods.includes("email")) {
        setMethod("email");
      }
    } catch (err) {
      console.error("Error fetching MFA status:", err);
    } finally {
      setLoading(false);
    }
  };

  const initiateChallenge = useCallback(
    async (selectedMethod: ChallengeMethod) => {
      if (selectedMethod === "backup") return;

      try {
        setError("");
        const { data } = await axios.post("/api/mfa/challenge", {
          method: selectedMethod,
        });

        // If trusted device was recognized, skip MFA
        if (data.trusted && data.mfaVerified) {
          setMfaVerified(true);
          window.location.href = isAgency ? "/agency/dashboard" : "/cases";
          return;
        }

        if (selectedMethod === "email") {
          setChallengeId(data.challengeId);
          setEmailSent(true);
        } else if (selectedMethod === "webauthn" && data.options) {
          // Auto-trigger WebAuthn
          try {
            const authResponse = await startAuthentication({
              optionsJSON: data.options,
            });
            await verifyWebAuthn(authResponse);
          } catch (err: any) {
            if (err.name === "NotAllowedError") {
              setError("Authentication was cancelled.");
            } else {
              setError("Device authentication failed. Try another method.");
            }
          }
        }
      } catch (err: any) {
        setError(err.response?.data?.error || "Failed to initiate challenge");
      }
    },
    [isAgency, navigate, setMfaVerified, trustDevice]
  );

  useEffect(() => {
    if (!loading && method) {
      initiateChallenge(method);
    }
  }, [loading, method]);

  const verifyWebAuthn = async (response: any) => {
    try {
      setVerifying(true);
      const { data } = await axios.post("/api/mfa/verify-webauthn", {
        response,
        trustDevice,
      });
      if (data.mfaVerified) {
        setMfaVerified(true);
        window.location.href = isAgency ? "/agency/dashboard" : "/cases";
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const handleVerify = async () => {
    if (!code) return;

    try {
      setVerifying(true);
      setError("");

      if (showBackup) {
        const { data } = await axios.post("/api/mfa/verify-backup", { code });
        if (data.mfaVerified) {
          setMfaVerified(true);
          window.location.href = isAgency ? "/agency/dashboard" : "/cases";
          return;
        }
      } else {
        const { data } = await axios.post("/api/mfa/verify", {
          code,
          method,
          challengeId,
          trustDevice,
        });
        if (data.mfaVerified) {
          setMfaVerified(true);
          window.location.href = isAgency ? "/agency/dashboard" : "/cases";
          return;
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Invalid code. Please try again.");
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  const handleMethodSwitch = (newMethod: ChallengeMethod) => {
    setMethod(newMethod);
    setCode("");
    setError("");
    setEmailSent(false);
    setChallengeId(null);
    setShowBackup(false);
  };

  const handleResendEmail = async () => {
    setEmailSent(false);
    await initiateChallenge("email");
  };

  // Auto-submit when 6 digits entered for TOTP/email
  useEffect(() => {
    if (code.length === 6 && !showBackup && (method === "totp" || method === "email")) {
      handleVerify();
    }
  }, [code]);

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen bg-base-200 flex flex-col items-center justify-center p-4">
      <div className="bg-base-100 rounded-xl shadow-sm border border-base-300 p-8 w-full max-w-md">
        <h1 className="text-xl font-semibold text-base-content mb-1 text-center">
          Verify Your Identity
        </h1>
        <p className="text-sm text-base-content/60 mb-6 text-center">
          {showBackup
            ? "Enter one of your backup recovery codes"
            : method === "totp"
            ? "Enter the code from your authenticator app"
            : method === "email"
            ? "Enter the code sent to your email"
            : "Authenticate with your device"}
        </p>

        <div
          className={`bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 text-sm rounded-lg p-3 mb-4 text-center transition-all duration-300 ease-in-out overflow-hidden ${
            error
              ? "max-h-20 opacity-100"
              : "max-h-0 opacity-0 !p-0 !mb-0 !border-0"
          }`}
        >
          {error}
        </div>

        {/* WebAuthn prompt */}
        {method === "webauthn" && !showBackup && (
          <div className="text-center py-6">
            <FingerPrintIcon className="w-16 h-16 mx-auto text-blue-500 dark:text-blue-300 mb-4" />
            <p className="text-sm text-gray-600 dark:text-base-content/70 mb-4">
              Use your device's biometric or security key to verify.
            </p>
            <Button
              variant="primary"
              onClick={() => initiateChallenge("webauthn")}
              className="w-full"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Code input for TOTP, email, and backup */}
        {(method === "totp" || method === "email" || showBackup) && (
          <div className="space-y-4">
            {method === "email" && emailSent && !showBackup && (
              <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-sm rounded-lg p-3">
                A code has been sent to your email.
              </div>
            )}

            <div>
              <Input
                type="text"
                inputMode={showBackup ? "text" : "numeric"}
                pattern={showBackup ? undefined : "[0-9]*"}
                maxLength={showBackup ? 8 : 6}
                value={code}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = showBackup
                    ? e.target.value
                    : e.target.value.replace(/\D/g, "");
                  setCode(val);
                }}
                placeholder={showBackup ? "backup code" : "000000"}
                className={`text-center w-full !max-w-none ${showBackup ? "text-lg tracking-wide" : "text-2xl tracking-widest"}`}
                autoFocus
                disabled={verifying}
              />
            </div>

            <Button
              variant="primary"
              onClick={handleVerify}
              disabled={
                verifying ||
                (!showBackup && code.length !== 6) ||
                (showBackup && code.length < 6)
              }
              loading={verifying}
              className="w-full"
            >
              {verifying ? "Verifying..." : "Verify"}
            </Button>

            {method === "email" && !showBackup && (
              <button
                onClick={handleResendEmail}
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300 w-full text-center"
              >
                Resend code
              </button>
            )}
          </div>
        )}

        {/* Trust device checkbox */}
        {trustedDeviceDays > 0 && (
          <label className="flex items-center justify-center gap-2 mt-4 cursor-pointer">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
              className="rounded border-gray-300 dark:border-base-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600 dark:text-base-content/70">
              Trust this device for {trustedDeviceDays} days
            </span>
          </label>
        )}

        {/* Method switcher */}
        <div className="mt-6 pt-4 border-t border-base-200 space-y-2">
          {!showBackup && availableMethods.length > 1 && (
            <div className="flex flex-wrap gap-2 justify-center">
              {availableMethods.includes("totp") && method !== "totp" && (
                <button
                  onClick={() => handleMethodSwitch("totp")}
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-300"
                >
                  Use authenticator app
                </button>
              )}
              {availableMethods.includes("email") && method !== "email" && (
                <button
                  onClick={() => handleMethodSwitch("email")}
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-300"
                >
                  Use email code
                </button>
              )}
              {availableMethods.includes("webauthn") &&
                method !== "webauthn" && (
                  <button
                    onClick={() => handleMethodSwitch("webauthn")}
                    className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-300"
                  >
                    Use device biometric
                  </button>
                )}
            </div>
          )}
          {/* With one method enrolled there's nothing to switch to, and the
              screen otherwise dead-ends. Enrolling here is deliberately not an
              option — issuing a new factor to someone who hasn't cleared the
              existing one is the bypass this flow exists to prevent. */}
          {!showBackup && availableMethods.length <= 1 && (
            <p className="text-xs text-base-content/60 text-center">
              Want a different method? Finish signing in, then add one under
              Settings &rarr; Security.
            </p>
          )}

          <button
            onClick={() => {
              setShowBackup(!showBackup);
              setCode("");
              setError("");
            }}
            className="text-xs text-base-content/60 hover:text-gray-700 dark:hover:text-base-content w-full text-center"
          >
            {showBackup ? "Back to verification" : "Use a backup code"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MFAChallenge;
